import { Injectable, EventEmitter, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subscription, filter, fromEvent, tap, BehaviorSubject } from 'rxjs';
import { PlatformUtils } from './platform.utils';
import { AppService, ApplicationMode } from './app.service';
import { AuthService } from '../auth-services/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { GlobalLoaderService, LoaderState } from '../core-services/global-loader.service';
import { GLOBAL_SHORTCUTS, NAVIGATION_SHORTCUTS, ROUTE_MAPPINGS } from './shortcuts.constants';
import { GlobalSearchService } from './global-search.service';
import { ContextRegistryService } from './context-registry.service';

export interface ShortcutCommand {
  id: string;
  description: string;
  keys: string[];
  formattedKeys: string;
  handler: () => void;
}

@Injectable({
  providedIn: 'root',
})
export class GlobalCommandsService implements OnDestroy {
  private readonly router = inject(Router);
  private readonly appService = inject(AppService);
  private readonly authService = inject(AuthService);
  private readonly dialog = inject(MatDialog);
  private readonly loaderService = inject(GlobalLoaderService);
  private readonly globalSearch = inject(GlobalSearchService);
  private readonly contextRegistry = inject(ContextRegistryService);
  private masterSubscription = new Subscription();
  private keySequenceTimeoutMs = 1000; // 1 second timeout for key sequences
  private shortcuts = new BehaviorSubject<ShortcutCommand[]>([]);
  private isMac = /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  private registeredCtrlShortcuts: Set<string> = new Set();

  public shortcutTriggered: EventEmitter<string> = new EventEmitter<string>();
  public shortcuts$ = this.shortcuts.asObservable();

  constructor() {
    // Create the base stream of keydown events
    const keydown$: Observable<KeyboardEvent> = fromEvent<KeyboardEvent>(document, 'keydown');

    // Filter for when user is not typing in input elements
    const globalShortcuts$ = keydown$.pipe(
      filter((event: KeyboardEvent) => {
        const target = event.target as HTMLElement;
        const tagName = target.tagName.toUpperCase();
        const isContentEditable = target.isContentEditable;

        // Allow Alt key shortcuts even in input fields
        if (event.altKey) {
          return true;
        }

        if (event.ctrlKey || event.metaKey) {
          const key = event.key.toLowerCase();
          if (this.registeredCtrlShortcuts.has(key)) {
            return true;
          }
          return false; // Let browser handle unregistered Ctrl shortcuts
        }

        return !['INPUT', 'TEXTAREA', 'SELECT'].includes(tagName) && !isContentEditable;
      })
    );

    this.initializeShortcuts(globalShortcuts$);
  }

  private initializeShortcuts(source$: Observable<KeyboardEvent>) {
    const registeredShortcuts: ShortcutCommand[] = [];

    // Register all global shortcuts using smart detection
    const globalShortcuts = [
      {
        keys: GLOBAL_SHORTCUTS.SEARCH.keys,
        description: GLOBAL_SHORTCUTS.SEARCH.description,
        handler: () => this.shortcutTriggered.emit('openSearch'),
      },
      {
        keys: GLOBAL_SHORTCUTS.THEME.keys,
        description: GLOBAL_SHORTCUTS.THEME.description,
        handler: () => this.appService.toggleThemeMode(),
      },
      {
        keys: GLOBAL_SHORTCUTS.MODE.keys,
        description: GLOBAL_SHORTCUTS.MODE.description,
        handler: () => {
          const currentMode = this.appService.getApplicationMode();
          const newMode =
            currentMode === ApplicationMode.Live ? ApplicationMode.Sandbox : ApplicationMode.Live;
          this.updateLoaderMessages(currentMode);
          this.appService.setApplicationMode(newMode);
          this.dialog.closeAll();
        },
      },
      {
        keys: GLOBAL_SHORTCUTS.LOGOUT.keys,
        description: GLOBAL_SHORTCUTS.LOGOUT.description,
        handler: async () => {
          await this.authService.logout();
          this.router.navigate(['auth', 'login']);
          this.dialog.closeAll();
        },
      },
      {
        keys: GLOBAL_SHORTCUTS.REFRESH_INDEX_CUSTOMER.keys,
        description: GLOBAL_SHORTCUTS.REFRESH_INDEX_CUSTOMER.description,
        handler: () => {
          this.masterSubscription.add(
            this.globalSearch.refreshCustomerIndex().subscribe({
              error: err => console.error('Failed to refresh customer index:', err),
            })
          );
        },
      },
      {
        keys: GLOBAL_SHORTCUTS.REFRESH_INDEX_ORDERS.keys,
        description: GLOBAL_SHORTCUTS.REFRESH_INDEX_ORDERS.description,
        handler: () => {
          this.masterSubscription.add(
            this.globalSearch.refreshOrderIndex().subscribe({
              error: err => console.error('Failed to refresh order index:', err),
            })
          );
        },
      },
      {
        keys: GLOBAL_SHORTCUTS.REFRESH_INDEX_PRODUCTS.keys,
        description: GLOBAL_SHORTCUTS.REFRESH_INDEX_PRODUCTS.description,
        handler: () => {
          this.masterSubscription.add(
            this.globalSearch.refreshProductIndex().subscribe({
              error: err => console.error('Failed to refresh product index:', err),
            })
          );
        },
      },
      {
        keys: GLOBAL_SHORTCUTS.SHORTCUTS.keys,
        description: GLOBAL_SHORTCUTS.SHORTCUTS.description,
        handler: () => this.shortcutTriggered.emit('show-shortcuts'),
      },
      {
        keys: GLOBAL_SHORTCUTS.DOWNLOADS.keys,
        description: GLOBAL_SHORTCUTS.DOWNLOADS.description,
        handler: () => this.shortcutTriggered.emit('toggleDownloads'),
      },
      {
        keys: GLOBAL_SHORTCUTS.SEARCH_CUSTOMERS.keys,
        description: GLOBAL_SHORTCUTS.SEARCH_CUSTOMERS.description,
        handler: () => this.shortcutTriggered.emit('searchCustomers'),
      },
      {
        keys: GLOBAL_SHORTCUTS.SEARCH_ORDERS.keys,
        description: GLOBAL_SHORTCUTS.SEARCH_ORDERS.description,
        handler: () => this.shortcutTriggered.emit('searchOrders'),
      },
      {
        keys: GLOBAL_SHORTCUTS.SEARCH_PRODUCTS.keys,
        description: GLOBAL_SHORTCUTS.SEARCH_PRODUCTS.description,
        handler: () => this.shortcutTriggered.emit('searchProducts'),
      },
      // Context-aware tab shortcuts
      {
        keys: GLOBAL_SHORTCUTS.NEXT_TAB.keys,
        description: GLOBAL_SHORTCUTS.NEXT_TAB.description,
        handler: () => this.contextRegistry.switchToNextTab(),
      },
      {
        keys: GLOBAL_SHORTCUTS.PREV_TAB.keys,
        description: GLOBAL_SHORTCUTS.PREV_TAB.description,
        handler: () => this.contextRegistry.switchToPrevTab(),
      },
      // Context-aware table shortcuts
      {
        keys: GLOBAL_SHORTCUTS.TABLE_SELECT_ALL.keys,
        description: GLOBAL_SHORTCUTS.TABLE_SELECT_ALL.description,
        handler: () => this.contextRegistry.selectAllTableRows(),
      },
      {
        keys: GLOBAL_SHORTCUTS.TABLE_DESELECT_ALL.keys,
        description: GLOBAL_SHORTCUTS.TABLE_DESELECT_ALL.description,
        handler: () => this.contextRegistry.deselectAllTableRows(),
      },
    ];

    // Collect all sequential shortcuts (global + navigation) for smart sequence matching
    const sequentialShortcuts = [
      // Global sequential shortcuts
      ...globalShortcuts
        .filter(
          shortcut =>
            shortcut.keys.length > 2 ||
            (shortcut.keys.length === 2 &&
              shortcut.keys[0] !== 'ctrl' &&
              shortcut.keys[0] !== 'alt')
        )
        .map(shortcut => ({
          keys: [...shortcut.keys],
          handler: shortcut.handler,
          description: shortcut.description,
        })),
      // Navigation shortcuts
      ...Object.values(NAVIGATION_SHORTCUTS).map(navItem => ({
        keys: [...navItem.keys],
        handler: () => {
          const shortcutId = navItem.keys.join('|');
          this.router.navigate([ROUTE_MAPPINGS[shortcutId]]);
        },
        description: `Navigate to ${navItem.name}`,
      })),
    ];

    // Register non-sequential shortcuts (ctrl/alt based) using individual registration
    globalShortcuts
      .filter(
        shortcut =>
          shortcut.keys.length === 2 && (shortcut.keys[0] === 'ctrl' || shortcut.keys[0] === 'alt')
      )
      .forEach(shortcut => {
        this.registerShortcutByType(
          source$,
          shortcut.keys,
          shortcut.description,
          shortcut.handler,
          registeredShortcuts
        );
      });

    // Register all sequential shortcuts with smart conflict resolution
    this.registerSequentialShortcuts(source$, sequentialShortcuts, registeredShortcuts);

    // Update shortcuts subject
    this.shortcuts.next(registeredShortcuts);
  }

  private updateLoaderMessages(currentMode: ApplicationMode) {
    if (currentMode === ApplicationMode.Live) {
      this.loaderService.setContent({
        title: '🔧 Switching to Sandbox Mode...',
        message:
          "Hold tight! We're setting up your sandbox for safe testing. You'll be back in the testing environment in no time!",
      });
    } else if (currentMode === ApplicationMode.Sandbox) {
      this.loaderService.setContent({
        title: '🚀 Switching to Live Mode...',
        message:
          "Get ready! We're powering up for live operations. This will just take a moment as we prepare everything for real-time action!",
      });
    }

    this.loaderService.setStatus(LoaderState.InProgress);
    setTimeout(() => {
      this.loaderService.setStatus(LoaderState.None);
    }, 1000);
  }

  private registerCtrlShortcut(
    source$: Observable<KeyboardEvent>,
    key: string,
    description: string,
    handler: () => void,
    registry: ShortcutCommand[]
  ) {
    // Add to registered shortcuts
    this.registeredCtrlShortcuts.add(key.toLowerCase());

    const keys = [this.isMac ? '⌘' : 'ctrl', key];
    const shortcut$ = source$.pipe(
      filter(event => {
        const modifierPressed = this.isMac ? event.metaKey : event.ctrlKey;
        return modifierPressed && !event.altKey && !event.shiftKey;
      }),
      filter(event => event.key.toLowerCase() === key.toLowerCase()),
      tap(event => {
        event.preventDefault();
        event.stopPropagation();
      })
    );

    const shortcutCommand: ShortcutCommand = {
      id: `ctrl_${key}`,
      description,
      keys,
      formattedKeys: PlatformUtils.formatShortcut(keys),
      handler,
    };

    registry.push(shortcutCommand);

    this.masterSubscription.add(
      shortcut$.subscribe(() => {
        handler();
      })
    );
  }

  private registerAltShortcut(
    source$: Observable<KeyboardEvent>,
    key: string,
    description: string,
    handler: () => void,
    registry: ShortcutCommand[]
  ) {
    const keys = [this.isMac ? '⌥' : 'alt', key];
    const shortcut$ = source$.pipe(
      filter(event => {
        const isAltOnly = event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey;

        // Handle different key types properly
        let isCorrectKey = false;

        // For letter keys (a-z)
        if (/^[a-zA-Z]$/.test(key)) {
          const expectedCode = `Key${key.toUpperCase()}`;
          isCorrectKey = event.code === expectedCode;
        }
        // For specific special characters
        else if (key === ';') {
          isCorrectKey = event.code === 'Semicolon';
        }
        // For other characters, fallback to event.key comparison
        else {
          isCorrectKey = event.key === key;
        }

        return isAltOnly && isCorrectKey;
      }),
      tap(event => {
        event.preventDefault();
        event.stopPropagation();
      })
    );

    const shortcutCommand: ShortcutCommand = {
      id: `alt_${key}`,
      description,
      keys,
      formattedKeys: PlatformUtils.formatShortcut(keys),
      handler,
    };

    registry.push(shortcutCommand);

    this.masterSubscription.add(
      shortcut$.subscribe(() => {
        handler();
      })
    );
  }

  private registerSequentialShortcuts(
    source$: Observable<KeyboardEvent>,
    shortcuts: Array<{ keys: string[]; handler: () => void; description: string }>,
    registry: ShortcutCommand[]
  ) {
    // Add all shortcuts to registry for UI display
    shortcuts.forEach(shortcut => {
      const shortcutCommand: ShortcutCommand = {
        id: shortcut.keys.join('_'),
        description: shortcut.description,
        keys: shortcut.keys,
        formattedKeys: PlatformUtils.formatSequentialShortcut(shortcut.keys),
        handler: shortcut.handler,
      };
      registry.push(shortcutCommand);
    });

    let keyBuffer: string[] = [];
    let timeoutId: any = null;

    const resetBuffer = () => {
      keyBuffer = [];
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const keyStream$ = source$.pipe(
      filter(event => {
        const target = event.target as HTMLElement;
        const tagName = target.tagName.toUpperCase();
        const isContentEditable = target.isContentEditable;
        const isInputField = ['INPUT', 'TEXTAREA', 'SELECT'].includes(tagName) || isContentEditable;

        // If it's an input field and not an Alt shortcut, ignore the key
        if (isInputField && !event.altKey) {
          resetBuffer(); // Reset the buffer when user starts typing in an input
          return false;
        }

        const key = event.key.toLowerCase();
        // Only process keys that are part of any registered sequence
        return shortcuts.some(shortcut => shortcut.keys.map(k => k.toLowerCase()).includes(key));
      }),
      tap(event => {
        event.preventDefault();
        const key = event.key.toLowerCase();

        // Reset the buffer if we're starting a new sequence
        if (keyBuffer.length === 0 || shortcuts.some(s => s.keys[0].toLowerCase() === key)) {
          // Only reset if this could be the start of a new sequence
          if (
            keyBuffer.length > 0 &&
            !shortcuts.some(
              s =>
                s.keys.length > keyBuffer.length &&
                s.keys
                  .slice(0, keyBuffer.length)
                  .every((k, i) => k.toLowerCase() === keyBuffer[i]) &&
                s.keys[keyBuffer.length].toLowerCase() === key
            )
          ) {
            resetBuffer();
          }
        }

        keyBuffer.push(key);

        // Clear any existing timeout
        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        // Find all matching shortcuts for current buffer
        const matchingShortcuts = shortcuts.filter(shortcut => {
          const shortcutKeys = shortcut.keys.map(k => k.toLowerCase());
          return (
            keyBuffer.length <= shortcutKeys.length &&
            keyBuffer.every((k, i) => k === shortcutKeys[i])
          );
        });

        // Check for exact matches
        const exactMatch = matchingShortcuts.find(
          shortcut => shortcut.keys.length === keyBuffer.length
        );

        // Check if there are longer sequences that could still match
        const longerSequencesPossible = matchingShortcuts.some(
          shortcut => shortcut.keys.length > keyBuffer.length
        );

        if (exactMatch && !longerSequencesPossible) {
          // We have an exact match and no longer sequences possible
          resetBuffer();
          exactMatch.handler();
        } else if (exactMatch && longerSequencesPossible) {
          // We have a match but longer sequences are possible, wait a bit
          timeoutId = setTimeout(() => {
            if (keyBuffer.length === exactMatch.keys.length) {
              resetBuffer();
              exactMatch.handler();
            }
          }, this.keySequenceTimeoutMs);
        } else if (!longerSequencesPossible) {
          // No matches possible, reset
          resetBuffer();
        } else {
          // Longer sequences possible, set timeout
          timeoutId = setTimeout(() => {
            resetBuffer();
          }, this.keySequenceTimeoutMs);
        }
      })
    );

    this.masterSubscription.add(keyStream$.subscribe());
  }

  private registerShortcutByType(
    source$: Observable<KeyboardEvent>,
    keys: readonly string[],
    description: string,
    handler: () => void,
    registry: ShortcutCommand[]
  ) {
    // Smart detection of shortcut type based on keys
    if (keys.length === 2) {
      const [modifier, key] = keys;

      if (modifier === 'ctrl') {
        this.registerCtrlShortcut(source$, key, description, handler, registry);
        return;
      }

      if (modifier === 'alt') {
        this.registerAltShortcut(source$, key, description, handler, registry);
        return;
      }
    }

    // Default to sequential shortcut for everything else
    this.registerSequentialShortcuts(
      source$,
      [{ keys: [...keys], handler, description }],
      registry
    );
  }

  ngOnDestroy(): void {
    this.masterSubscription.unsubscribe();
  }
}
